import { z } from "zod";

import { ANNOTATION_STATUSES, LANGUAGES, ROLES, SUBJECT_TYPES } from "./types";

/** A `M#C#D#` doha reference. */
export const dohaSlugSchema = z
  .string()
  .trim()
  .regex(/^M\d+C\d+D\d+$/, "Expected a doha reference like M2C5D3");

/** A glossary term slug (lowercase, hyphenated). */
export const termSlugSchema = z.string().trim().min(1).max(160);

// ── Glossary ────────────────────────────────────────────────────────
export const searchInputSchema = z.object({
  q: z.string().trim().min(1).max(120),
  limit: z.number().int().min(1).max(50).default(20),
});

export const termInputSchema = z.object({ slug: termSlugSchema });

export const graphInputSchema = z.object({
  focusSlug: z.string().trim().min(1).optional(),
  mandal: z.number().int().min(1).max(20).optional(),
  depth: z.number().int().min(1).max(2).default(1),
  limit: z.number().int().min(10).max(600).default(250),
});

// ── Dohas ───────────────────────────────────────────────────────────
export const dohaGetInputSchema = z.object({ slug: dohaSlugSchema });

export const dohaListInputSchema = z.object({
  mandal: z.number().int().min(1).max(20).optional(),
  chapter: z.number().int().min(1).max(50).optional(),
  limit: z.number().int().min(1).max(200).default(100),
  cursor: z.number().int().min(0).optional(),
});

// ── Captures ────────────────────────────────────────────────────────
export const uploadUrlInputSchema = z.object({
  contentType: z
    .string()
    .regex(
      /^image\/(jpeg|png|webp|heic|heif)$/,
      "Only image uploads are allowed",
    ),
});

export const createCaptureInputSchema = z.object({
  dohaSlug: dohaSlugSchema.optional(),
  language: z.enum(LANGUAGES),
  r2Key: z.string().min(1).max(256),
  note: z.string().trim().max(2000).optional(),
  width: z.number().int().positive().max(20000).optional(),
  height: z.number().int().positive().max(20000).optional(),
});

export const idInputSchema = z.object({ id: z.number().int().positive() });

export const mineInputSchema = z.object({
  cursor: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(60).default(24),
});

// ── Annotations ─────────────────────────────────────────────────────
export const annotationBoxSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
});

export const createAnnotationInputSchema = z
  .object({
    captureId: z.number().int().positive(),
    status: z.enum(ANNOTATION_STATUSES),
    /** The word as it appears / was typed. Required so an undefined
     *  request always names the word even before a term exists. */
    label: z.string().trim().min(1).max(160),
    /** When status = linked, the glossary term this word maps to. */
    termSlug: termSlugSchema.optional(),
    box: annotationBoxSchema.optional(),
  })
  .refine((v) => v.status !== "linked" || Boolean(v.termSlug), {
    message: "A linked annotation needs a termSlug",
    path: ["termSlug"],
  });

export const resolveAnnotationInputSchema = z.object({
  id: z.number().int().positive(),
  termSlug: termSlugSchema,
});

// ── Threads / messages ──────────────────────────────────────────────
export const threadRefInputSchema = z.object({
  subjectType: z.enum(SUBJECT_TYPES),
  subjectId: z.number().int().positive(),
});

export const postMessageInputSchema = z.object({
  threadId: z.number().int().positive(),
  body: z.string().trim().min(1).max(4000),
});

// ── Helper / admin ──────────────────────────────────────────────────
export const cursorInputSchema = z.object({
  cursor: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(60).default(25),
});

export const answerInputSchema = z.object({
  annotationId: z.number().int().positive(),
  definition: z.string().trim().min(1).max(4000),
  /** Optional doha context the sense belongs to. */
  dohaSlug: dohaSlugSchema.optional(),
  /** Link the annotation to this term once answered. When omitted and
   *  the annotation names a new word, the term is derived from label. */
  termSlug: termSlugSchema.optional(),
  resolve: z.boolean().default(true),
});

export const setRoleInputSchema = z.object({
  userId: z.string().min(1),
  /** null = revoke access (back to pending approval). */
  role: z.enum(ROLES).nullable(),
});
